import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { lcCoachingApi } from '@/api/lcCoaching'
import { getApiErrorMessage } from '@/utils/apiError'
import { coachingKeys } from '@/hooks/coaching/useCoachingToday'
import type { WorkbookGradeRequest, WorkbookGradeView } from '@/types/coaching'

/**
 * 워크북 채점 조회 (G7) — 재채점 prefill. 워크북은 디지털 quiz 와 격리된 전용 레인이라
 * LearnerTodayResponse(component_progress) 에 없고 별도 쿼리로 읽는다.
 */
export function useWorkbookGrades(learnerId: number, lessonId: number | null, enabled = true) {
  return useQuery<WorkbookGradeView[]>({
    queryKey: coachingKeys.workbookGrades(learnerId, lessonId ?? 0),
    queryFn: () => lcCoachingApi.getWorkbookGrades(learnerId, lessonId as number),
    enabled: enabled && lessonId !== null,
  })
}

/**
 * 워크북 채점 저장 mutation (G7) — 레슨 단위 batch 멱등 upsert.
 * 전용 테이블 기록이라 onSuccess 시 해당 워크북 쿼리만 무효화(진도/정답률 집계 무관).
 */
export function useGradeWorkbook(learnerId: number, lessonId: number) {
  const qc = useQueryClient()
  const { t } = useTranslation('coaching')

  return useMutation({
    mutationFn: (body: WorkbookGradeRequest) => lcCoachingApi.gradeWorkbook(learnerId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: coachingKeys.workbookGrades(learnerId, lessonId) })
      toast.success(t('workbook.saved'))
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, t))
    },
  })
}
